import { flowResult, makeAutoObservable, runInAction } from "mobx";
import { Money } from "../../core";
import { localizePath } from "../../locale";
import { hasDeliveryInfo, } from "../../ordering";
import { GeolocationTracking } from "../../tracking";
import { paymentMethodLabel } from "../paymentMethodLabel";
import { StreetAddressFormController } from "./StreetAddressFormController";
/** `contact_number` is a free-form field; both `,` and `;` are used as separators. */
function parsePhones(contactPhoneNumber) {
    if (contactPhoneNumber === null) {
        return [];
    }
    return contactPhoneNumber
        .split(/[,;]/)
        .map((phone) => phone.trim())
        .filter((phone) => phone !== "");
}
/**
 * Drives the "delivery checker" widget on `/dostawa` (and the vesspa home
 * hero): the eater enters an address and learns which restaurant delivers
 * there, at what cost. Replaces the legacy PureScript
 * `Components.DeliveryForm` + `Site.Components.DeliveryAddress` widgets.
 */
export class DeliveryCheckerController {
    constructor(params) {
        this._contacts = [];
        this.streetAddress = params.streetAddress;
        this.deliveryAddressStatus = params.deliveryAddressStatus;
        this.siteRestaurantsService = params.siteRestaurantsService;
        this.localizationProvider = params.localizationProvider;
        this.streetAddressStorageManager = params.streetAddressStorageManager;
        this.streetAddressController = new StreetAddressFormController({
            streetAddress: this.streetAddress,
            streetAddressAvailabilityService: params.streetAddressAvailabilityService,
        });
        makeAutoObservable(this);
    }
    get state() {
        return this.deliveryAddressStatus.state;
    }
    get isChecking() {
        return this.state.type === "Loading";
    }
    get hasGeocodingError() {
        return (this.state.type === "AddressDoesNotExistOrInaccurate" ||
            this.state.type === "NetworkError");
    }
    /** The address is valid and geocodable, but no restaurant delivers there. */
    get noRestaurantDelivers() {
        return this.state.type === "RestaurantDoesntDeliverTo";
    }
    /**
     * Only the nearest delivering restaurant is offered, never a list.
     * `GetRestaurants` sorts by delivery distance, so the head is the nearest.
     */
    get deliveringRestaurant() {
        const state = this.state;
        if (state.type !== "ChooseRestaurant" &&
            state.type !== "RestaurantDelivers") {
            return null;
        }
        const nearest = state.restaurants.filter(hasDeliveryInfo)[0];
        return nearest === undefined ? null : this.toDeliveringRestaurant(nearest);
    }
    get contacts() {
        return this._contacts;
    }
    async check() {
        this.streetAddressController.submit();
        this.storeAddress();
        await flowResult(this.deliveryAddressStatus.validate(null, this.streetAddress));
        GeolocationTracking.findAddressEvent("DeliveryChecker", this.state, this.streetAddress);
        if (this.noRestaurantDelivers) {
            await this.loadContacts();
        }
    }
    /**
     * Persisting is best-effort: `localStorage` throws in Safari private mode, and
     * that must not abort the delivery check.
     */
    storeAddress() {
        try {
            this.streetAddressStorageManager.store(this.streetAddress.storageData);
        }
        catch {
            // Ignore — the address is only cached for convenience.
        }
    }
    async loadContacts() {
        const restaurants = await this.siteRestaurantsService.getSiteRestaurants();
        runInAction(() => {
            this._contacts = restaurants.map((r) => ({
                name: r.name,
                phones: parsePhones(r.contactPhoneNumber),
            }));
        });
    }
    toDeliveringRestaurant(restaurant) {
        var _a;
        const info = restaurant.deliveryInfo;
        return {
            id: restaurant.id,
            slug: restaurant.slug,
            orderPath: localizePath(`/restauracja/${(_a = restaurant.slug) !== null && _a !== void 0 ? _a : ""}`),
            deliveryCost: this.localizationProvider.formatPrice(Money.fromBackend(info.deliveryCost)),
            minOrderValue: this.localizationProvider.formatPrice(Money.fromBackend(info.minOrderValue)),
            freeDeliveryThreshold: info.freeDeliveryThreshold !== null
                ? this.localizationProvider.formatPrice(Money.fromBackend(info.freeDeliveryThreshold))
                : null,
            paymentMethods: restaurant.paymentMethods.map(paymentMethodLabel),
        };
    }
}
